/**
 * 途经点步骤条组件
 *
 * 根据途经点类型（起点/途经点/终点）展示不同的操作步骤，以圆点+箭头形式展示进度。
 * 紫色实心表示已完成，灰色空心表示未完成，节点可点击。
 * 点击步骤时会校验前置条件，不满足则弹窗提示。
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { message } from 'antd';
import { t } from '../../../../i18n';
import {
    GET_HAS_TMS_LOADING_DETAIL,
    HasTmsLoadingDetailResponse,
    HasTmsLoadingDetailVariables,
} from '../graphql/queries/getHasTmsLoadingDetail';
import {
    GET_TMS_WAYPOINT_LOADING_INFO_LIST,
    type GetTmsWaypointLoadingInfoListResponse,
    type GetTmsWaypointLoadingInfoListVariables,
} from '../graphql/queries/getTmsWaypointLoadingInfoList';
import { TmsTaskWaypointStatusEnum, WaypointTypeEnum, TmsTaskStatusEnum } from '../../../../enums/TmsEnum';
import type { TmsTaskWaypointType, TmsTaskDetailType } from '../graphql/queries/getTmsTaskDetail';
import { StepKey, StepConfig, STEP_LABEL_KEYS, resolveSteps } from '../config/taskTabStepConfig';
import { isEditDisabledByConfig } from '../config/taskEditDisabledConfig';
import DriverArrivedDetail from './step-details/DriverArrivedDetail';
import LoadingDetail from './step-details/LoadingDetail';
import SealingDetail from './step-details/SealingDetail';
import DepartureDetail from './step-details/DepartureDetail';
import UnsealingDetail from './step-details/UnsealingDetail';
import UnloadingDetail from './step-details/UnloadingDetail';

/** 组件 Props */
interface WaypointStepBarProps {
    waypoint: TmsTaskWaypointType;
    onStepClick?: (index: number, step: StepConfig) => void;
    onOpenLoadingDetailDrawer?: (waypointId: number) => void;
    onOpenUnloadingDetailDrawer?: (waypointId: number) => void;
    /** 可选，来自 GetTmsTaskDetail 的 task */
    task?: TmsTaskDetailType;
    /** 可选，全量途经点列表，用于途径站校验「上一站已发车」 */
    waypoints?: TmsTaskWaypointType[];
}



type StepDetailProps = {
    waypoint: TmsTaskWaypointType;
    /** 步骤详情编辑：canEdit × taskEditDisabledConfig 配置 */
    stepDetailAllowEdit: boolean;
    onOpenLoadingDetailDrawer?: (waypointId: number) => void;
    onOpenUnloadingDetailDrawer?: (waypointId: number) => void;
    task?: TmsTaskDetailType;
};
const STEP_DETAIL_COMPONENTS: Record<StepKey, React.ComponentType<StepDetailProps>> = {
    [StepKey.DriverArrived]: DriverArrivedDetail,
    [StepKey.Loading]: LoadingDetail,
    [StepKey.Sealing]: SealingDetail,
    [StepKey.Departure]: DepartureDetail,
    [StepKey.Unsealing]: UnsealingDetail,
    [StepKey.Unloading]: UnloadingDetail,
};

/** 箭头列宽度（px），可手动调整 */
const ARROW_COLUMN_WIDTH = 115;

/** 标签列宽度（px），可手动调整 */
const LABEL_COLUMN_WIDTH = 90;

/** 是否已完成解封车：waypoint_status=2 且 un_seal_time 不为空 */
function isUnsealingDone(waypoint: TmsTaskWaypointType): boolean {
    return (
        waypoint.waypointStatus >= TmsTaskWaypointStatusEnum.Arrived &&
        !!waypoint.unSealTime &&
        String(waypoint.unSealTime).trim() !== ''
    );
}

/** 布尔化字符串字段 */
function hasTime(value?: string | null): boolean {
    return !!value && String(value).trim() !== '';
}

/** 根据实际数据判断某个步骤是否已完成，用于「点亮」 */
function isStepCompletedForDisplay(
    waypointType: number,
    stepKey: StepKey,
    waypoint: TmsTaskWaypointType
): boolean {
    const status = waypoint.waypointStatus;
    const arrived = status >= TmsTaskWaypointStatusEnum.Arrived;
    const unsealed = arrived && hasTime(waypoint.unSealTime);
    const unloaded = arrived && hasTime(waypoint.unLoadingTime);
    const loaded = arrived && hasTime(waypoint.loadingTime);
    const sealed =
        status >= TmsTaskWaypointStatusEnum.PENDING_DEPARTURE || hasTime(waypoint.sealTime);
    const departed = status === TmsTaskWaypointStatusEnum.Done;

    switch (waypointType) {
        case WaypointTypeEnum.Origin:
            // 起点：司机到达 → 装车 → 封车 → 发车
            switch (stepKey) {
                case StepKey.DriverArrived:
                    return arrived;
                case StepKey.Loading:
                    return loaded;
                case StepKey.Sealing:
                    return sealed;
                case StepKey.Departure:
                    return departed;
                default:
                    return false;
            }
        case WaypointTypeEnum.Waypoint:
            // 途径站：司机到达 → 解封车 → 卸车 → 装车 → 封车 → 发车
            switch (stepKey) {
                case StepKey.DriverArrived:
                    return arrived;
                case StepKey.Unsealing:
                    return unsealed;
                case StepKey.Unloading:
                    return unloaded;
                case StepKey.Loading:
                    return loaded;
                case StepKey.Sealing:
                    return sealed;
                case StepKey.Departure:
                    return departed;
                default:
                    return false;
            }
        case WaypointTypeEnum.Destination:
            // 终到站：司机到达 → 解封车 → 卸车
            switch (stepKey) {
                case StepKey.DriverArrived:
                    return arrived;
                case StepKey.Unsealing:
                    return unsealed;
                case StepKey.Unloading:
                    return unloaded;
                default:
                    return false;
            }
        default:
            return false;
    }
}

/** 步骤点击校验结果 */
interface StepValidationResult {
    allowed: boolean;
    message?: string;
}

/**
 * 校验当前点击的步骤是否允许操作（前置步骤是否已完成）
 * waypointStatus: 1 待到站 2 已到站 4 待发车 3 已发车
 * @param waypointType 途经点类型（1-起点、2-途经点、3-终点）
 * @param stepKey 步骤节点类型（司机到达、解封车、装车、卸车、封车、发车）
 * @param waypoint 途经点信息
 * @param prevWaypoint 上一个途经点信息
 * @param task 可选，有 task 时始发站封车由调用方通过 hasTmsLoadingDetail 接口校验
 */
function validateStepClick(
    waypointType: number,
    stepKey: StepKey,
    waypoint: TmsTaskWaypointType,
    prevWaypoint: TmsTaskWaypointType | null,
    task?: TmsTaskDetailType
): StepValidationResult {
    // 草稿、已完成、已取消：不校验前置步骤，允许自由切换步骤查看（已取消任务只读，装车明细已被整车移出）
    if (
        task?.taskStatus === TmsTaskStatusEnum.Draft ||
        task?.taskStatus === TmsTaskStatusEnum.Completed ||
        task?.taskStatus === TmsTaskStatusEnum.Cancelled
    ) {
        return { allowed: true };
    }

    const status = waypoint.waypointStatus;

    const incompleteStepMsg = (labelKey: string) =>
        t('tms.task.stepValidation.incompleteStep', { step: String(t(labelKey)) });

    if (waypointType === WaypointTypeEnum.Origin) {
        // waypointType=1 始发站
        // 顺序：司机到达 → 装车 → 封车 → 发车
        switch (stepKey) {
            case StepKey.DriverArrived:
                // 司机到达节点始终允许点击
                return { allowed: true };
            case StepKey.Loading:
                // 已到站(waypoint_status>=2) 才能装车
                if (status >= TmsTaskWaypointStatusEnum.Arrived) return { allowed: true };
                return { allowed: false, message: incompleteStepMsg(STEP_LABEL_KEYS[StepKey.DriverArrived]) };
            case StepKey.Sealing:
                // 封车前必须先装车：未完成装车时，提示“未完成装车，请优先装车”
                if (task) {
                    return { allowed: true };
                }
                if (!isStepCompletedForDisplay(WaypointTypeEnum.Origin, StepKey.Loading, waypoint)) {
                    return { allowed: false, message: incompleteStepMsg(STEP_LABEL_KEYS[StepKey.Loading]) };
                }
                return { allowed: true };
            case StepKey.Departure:
                // 发车需校验已经封车：封车后 waypoint_status 从 2 变为 4，再从 4 变 3
                if (
                    status === TmsTaskWaypointStatusEnum.PENDING_DEPARTURE ||
                    status === TmsTaskWaypointStatusEnum.Done
                ) {
                    return { allowed: true };
                }
                return { allowed: false, message: incompleteStepMsg(STEP_LABEL_KEYS[StepKey.Sealing]) };
            default:
                return { allowed: true };
        }
    }

    if (waypointType === WaypointTypeEnum.Destination) {
        // waypointType=3 终到站
        // 顺序：司机到达 → 解封车 → 卸车
        switch (stepKey) {
            case StepKey.DriverArrived:
                return { allowed: true };
            case StepKey.Unsealing:
                // 司机到达(status>=2) 才能解封车
                if (status >= TmsTaskWaypointStatusEnum.Arrived) return { allowed: true };
                return { allowed: false, message: incompleteStepMsg(STEP_LABEL_KEYS[StepKey.DriverArrived]) };
            case StepKey.Unloading:
                // 解封车完成(waypoint_status=2 且 un_seal_time 不为空) 才能卸车
                if (isUnsealingDone(waypoint)) return { allowed: true };
                return { allowed: false, message: incompleteStepMsg(STEP_LABEL_KEYS[StepKey.Unsealing]) };
            default:
                return { allowed: true };
        }
    }

    if (waypointType === WaypointTypeEnum.Waypoint) {
        // waypointType=2 途径站
        // 顺序：司机到达 → 解封车 → 卸车 → 装车 → 封车 → 发车
        switch (stepKey) {
            case StepKey.DriverArrived:
                // ① 上一站发车之后，才可以操作到达
                if (!prevWaypoint) return { allowed: true };
                if (prevWaypoint.waypointStatus === TmsTaskWaypointStatusEnum.Done) return { allowed: true };
                return { allowed: false, message: t('tms.task.stepValidation.prevStationNotDeparted') };
            case StepKey.Unsealing:
                // ② 司机到达(status>=2)，才可以操作解封车
                if (status >= TmsTaskWaypointStatusEnum.Arrived) return { allowed: true };
                return { allowed: false, message: incompleteStepMsg(STEP_LABEL_KEYS[StepKey.DriverArrived]) };
            case StepKey.Unloading:
            case StepKey.Loading:
                // ③ 解封车完成可操作卸车和装车
                if (isUnsealingDone(waypoint)) return { allowed: true };
                return { allowed: false, message: incompleteStepMsg(STEP_LABEL_KEYS[StepKey.Unsealing]) };
            case StepKey.Sealing:
                // ④ 封车时，只校验解封车节点即可
                if (isUnsealingDone(waypoint)) return { allowed: true };
                return { allowed: false, message: incompleteStepMsg(STEP_LABEL_KEYS[StepKey.Unsealing]) };
            case StepKey.Departure: {
                // ⑤ 发车校验：根据步骤配置决定前置条件
                const waypointSteps = resolveSteps(waypointType, Number(task?.routeType), task?.businessType != null ? Number(task.businessType) : undefined, task?.warehouseOperationSimplify === true);
                const hasSealing = waypointSteps.includes(StepKey.Sealing);
                if (hasSealing) {
                    // 有封车节点：需校验是否已封车（waypoint_status=4 或 3）
                    if (
                        status === TmsTaskWaypointStatusEnum.PENDING_DEPARTURE ||
                        status === TmsTaskWaypointStatusEnum.Done
                    ) {
                        return { allowed: true };
                    }
                    return { allowed: false, message: incompleteStepMsg(STEP_LABEL_KEYS[StepKey.Sealing]) };
                } else {
                    // 无封车节点（如 TemuSCF 途经点）：只需司机已到达
                    if (status >= TmsTaskWaypointStatusEnum.Arrived) return { allowed: true };
                    return { allowed: false, message: incompleteStepMsg(STEP_LABEL_KEYS[StepKey.DriverArrived]) };
                }
            }
            default:
                return { allowed: true };
        }
    }

    return { allowed: true };
}

/** 步骤之间的连接箭头 */
const ConnectorArrow: React.FC<{ isCompleted: boolean }> = ({ isCompleted }) => (
    <div className="flex items-center flex-shrink-0" style={{ width: ARROW_COLUMN_WIDTH }}>
        <div className={`flex-1 h-0.5 ${isCompleted ? 'bg-[#722ed1]' : 'bg-gray-300'}`} />
        <div
            className="w-0 h-0 border-t-[6px] border-b-[6px] border-l-[10px] border-t-transparent border-b-transparent flex-shrink-0"
            style={{ borderLeftColor: isCompleted ? '#722ed1' : '#d9d9d9' }}
        />
    </div>
);

const WaypointStepBar: React.FC<WaypointStepBarProps> = ({ waypoint, onStepClick, onOpenLoadingDetailDrawer, onOpenUnloadingDetailDrawer, task, waypoints }) => {
    // 任务是否可编辑（草稿和已取消状态不支持编辑）
    const canEdit = useMemo(() => {
        const status = task?.taskStatus;
        return status !== TmsTaskStatusEnum.Draft && status !== TmsTaskStatusEnum.Cancelled;
    }, [task?.taskStatus]);

    const stepDetailAllowEdit = useMemo(
        () => {
            if (!canEdit) return false;
            const waypointType = waypoint.waypointType ?? WaypointTypeEnum.Origin;
            return !isEditDisabledByConfig(waypointType, waypoint.waypointStatus, Number(task?.businessType), Number(task?.routeType));
        },
        [canEdit, task?.businessType, waypoint.waypointType, waypoint.waypointStatus],
    );
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [hasTmsLoadingDetailQuery] = useLazyQuery<
        HasTmsLoadingDetailResponse,
        HasTmsLoadingDetailVariables
    >(GET_HAS_TMS_LOADING_DETAIL);

    const waypointType = waypoint.waypointType ?? WaypointTypeEnum.Origin;
    const shouldFetchLoadingDetail = waypointType === WaypointTypeEnum.Origin && !!task;
    const { data: hasLoadingDetailData, refetch: refetchHasLoadingDetail } = useQuery<
        HasTmsLoadingDetailResponse,
        HasTmsLoadingDetailVariables
    >(GET_HAS_TMS_LOADING_DETAIL, {
        variables: {
            taskId: Number(task?.id) || waypoint.taskId,
            taskWayPointId: Number(waypoint.id),
        },
        skip: !shouldFetchLoadingDetail,
    });
    const hasLoadingDetailFromApi = hasLoadingDetailData?.hasTmsLoadingDetailForPortal;
    // 装车列表（含已移出行）：任务取消 / 全部移出后当前装车行为 0，但该站点装过车，「装车」仍应点亮。
    // 变量与 LoadingDetail 一致以共享 Apollo 缓存；封车拦截仍只认 hasTmsLoadingDetailForPortal（当前装车行）
    const { data: loadingInfoListData } = useQuery<
        GetTmsWaypointLoadingInfoListResponse,
        GetTmsWaypointLoadingInfoListVariables
    >(GET_TMS_WAYPOINT_LOADING_INFO_LIST, {
        variables: { taskId: waypoint.taskId, taskWaypointId: Number(waypoint.id) },
        skip: !shouldFetchLoadingDetail,
    });
    const hasEverLoaded = (loadingInfoListData?.getTmsWaypointLoadingInfoList?.length ?? 0) > 0;

    // 当 loadingTime 发生变化时（即装车保存成功后父组件刷新了 waypoint 数据），
    // 主动触发 hasTmsLoadingDetail 接口重新请求，确保装车圆圈及时变色
    useEffect(() => {
        if (shouldFetchLoadingDetail) {
            refetchHasLoadingDetail();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waypoint.loadingTime]);

    /** 始发站装车步骤：有当前装车行，或装过车（含已移出）即点亮；不兜底本地 loadingTime */
    const isStepCompletedResolved = useMemo(() => {
        return (stepKey: StepKey): boolean => {
            if (
                waypointType === WaypointTypeEnum.Origin &&
                stepKey === StepKey.Loading &&
                shouldFetchLoadingDetail
            ) {
                return hasLoadingDetailFromApi === true || hasEverLoaded;
            }
            return isStepCompletedForDisplay(waypointType, stepKey, waypoint);
        };
    }, [
        waypointType,
        waypoint,
        shouldFetchLoadingDetail,
        hasLoadingDetailFromApi,
        hasEverLoaded,
    ]);

    const steps = resolveSteps(waypointType, Number(task?.routeType), task?.businessType != null ? Number(task.businessType) : undefined, task?.warehouseOperationSimplify === true);

    const stepConfigs: StepConfig[] = useMemo(
        () => steps.map((key) => ({ key, labelKey: STEP_LABEL_KEYS[key] })),
        [steps]
    );

    const prevWaypoint = useMemo(() => {
        if (!waypoints || waypoints.length === 0) return null;
        const idx = waypoints.findIndex((wp) => wp.id === waypoint.id);
        if (idx <= 0) return null;
        return waypoints[idx - 1] ?? null;
    }, [waypoints, waypoint.id]);

    // 根据当前 waypoint 的完成情况，自动选择「当前进行中的节点」
    // 规则：
    // 1) 若状态为 待发车/已发车，则直接落在“发车”节点（起点/途经点），终点则落在最后一个节点
    // 2) 否则，从左到右找到第一个未完成的节点；若全部完成，则选最后一个
    // 依赖仅 waypoint.id：切换途经点时重新计算；同一途经点下数据刷新（如保存装车后 refetch）不触发，保持用户当前选中
    useEffect(() => {
        if (!stepConfigs.length) {
            setSelectedIndex(null);
            return;
        }

        const status = waypoint.waypointStatus;

        // 已经封车（待发车）或已发车时，优先落在“发车”节点
        if (
            status === TmsTaskWaypointStatusEnum.PENDING_DEPARTURE ||
            status === TmsTaskWaypointStatusEnum.Done
        ) {
            const departureIdx = stepConfigs.findIndex((cfg) => cfg.key === StepKey.Departure);
            if (departureIdx >= 0) {
                setSelectedIndex(departureIdx);
                return;
            }
            // 终点（无发车）等场景，退回到最后一个步骤
            setSelectedIndex(stepConfigs.length - 1);
            return;
        }

        let firstIncompleteIndex = stepConfigs.findIndex(
            (cfg) => !isStepCompletedResolved(cfg.key)
        );

        if (firstIncompleteIndex === -1) {
            firstIncompleteIndex = stepConfigs.length - 1;
        }

        setSelectedIndex(firstIncompleteIndex);
    }, [waypoint.id]);

    const handleStepClick = async (index: number, step: StepConfig) => {
        if (selectedIndex === index) return;

        const validation = validateStepClick(
            waypointType,
            step.key,
            waypoint,
            prevWaypoint,
            task
        );
        if (!validation.allowed) {
            message.warning(validation.message);
            return;
        }

        // 始发站封车：调用后端接口校验是否已完成装车（草稿/已完成/已取消不校验：已取消任务只读，装车明细已被整车移出）
        const skipLoadingDetailCheck =
            task?.taskStatus === TmsTaskStatusEnum.Draft ||
            task?.taskStatus === TmsTaskStatusEnum.Completed ||
            task?.taskStatus === TmsTaskStatusEnum.Cancelled;
        if (
            !skipLoadingDetailCheck &&
            waypointType === WaypointTypeEnum.Origin &&
            step.key === StepKey.Sealing &&
            task
        ) {
            const taskId = Number(task.id) || waypoint.taskId;
            const taskWayPointId = Number(waypoint.id);
            const { data } = await hasTmsLoadingDetailQuery({
                variables: { taskId, taskWayPointId },
            });
            if (!data?.hasTmsLoadingDetailForPortal) {
                message.warning(
                    t('tms.task.stepValidation.incompleteStep', {
                        step: String(t(STEP_LABEL_KEYS[StepKey.Loading])),
                    })
                );
                return;
            }
        }

        setSelectedIndex(index);
        onStepClick?.(index, step);
    };

    const selectedStep = selectedIndex !== null ? stepConfigs[selectedIndex] : null;
    const DetailComponent = selectedStep ? STEP_DETAIL_COMPONENTS[selectedStep.key] : null;

    return (
        <div className="py-6">
            {/* 圆点、中间空隙、标签 整列可点击，样式保持不变 */}
            <div className="flex items-start">
                {stepConfigs.map((step, index) => {
                    const isActive = isStepCompletedResolved(step.key);
                    // 对途经点：当已封车但未做卸车/装车时，这两个节点视为“跳过”，显示为实心灰色
                    const sealed =
                        waypoint.waypointStatus >= TmsTaskWaypointStatusEnum.PENDING_DEPARTURE ||
                        hasTime(waypoint.sealTime);
                    const isOptionalLoadOrUnloadStep =
                        waypointType === WaypointTypeEnum.Waypoint &&
                        (step.key === StepKey.Unloading || step.key === StepKey.Loading);
                    const isSkippedOptionalStep =
                        sealed && isOptionalLoadOrUnloadStep && !isActive;

                    const isSelected = selectedIndex === index;
                    const isLast = index === stepConfigs.length - 1;
                    return (
                        <React.Fragment key={step.key}>
                            <div
                                role="button"
                                tabIndex={0}
                                onClick={() => handleStepClick(index, step)}
                                onKeyDown={(e) => e.key === 'Enter' && handleStepClick(index, step)}
                                className="flex flex-col items-center flex-shrink-0 cursor-pointer"
                                style={{ width: LABEL_COLUMN_WIDTH }}
                            >
                                <span
                                    className={`inline-block w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                                        isActive
                                            ? 'bg-[#722ed1] border-[#722ed1]'
                                            : isSkippedOptionalStep
                                            ? 'bg-gray-300 border-gray-300'
                                            : 'bg-white border-gray-300'
                                    }`}
                                />
                                <div className={`mt-2 w-full flex flex-col items-center overflow-hidden flex-1 min-h-0 ${index === 0 ? 'rounded-tl-md' : ''} ${index === stepConfigs.length - 1 ? 'rounded-tr-md' : ''} rounded-t-md`}>
                                    <span
                                        className={`text-sm text-center whitespace-nowrap py-2 transition-colors block w-full ${
                                            isSelected ? 'min-w-[110px] bg-gray-100' : 'min-w-[100px]'
                                        } ${
                                            isActive
                                                ? 'text-gray-800 font-medium'
                                                : isSkippedOptionalStep
                                                ? 'text-gray-500'
                                                : 'text-gray-400'
                                        }`}
                                    >
                                        {String(t(step.labelKey))}
                                    </span>
                                </div>
                            </div>
                            {!isLast && <ConnectorArrow isCompleted={isActive} />}
                        </React.Fragment>
                    );
                })}
            </div>
            {selectedStep && (
        <div
            className={`w-full min-h-[120px] bg-gray-100 border-gray-200 px-4 py-4 ${
                selectedIndex === 0
                    ? 'rounded-tr-md rounded-br-md rounded-bl-md'
                    : selectedIndex === stepConfigs.length - 1
                    ? 'rounded-tl-md rounded-tr-md rounded-bl-md rounded-br-md'
                    : 'rounded-md'
            }`}
        >
            {DetailComponent && (
                <DetailComponent
                    waypoint={waypoint}
                    stepDetailAllowEdit={stepDetailAllowEdit}
                    onOpenLoadingDetailDrawer={onOpenLoadingDetailDrawer}
                    onOpenUnloadingDetailDrawer={onOpenUnloadingDetailDrawer}
                    task={task}
                />
            )}
        </div>
            )}
        </div>
    );
};

export default WaypointStepBar;
